import React, { useState, useEffect } from "react";
import { TarpanaStep, Mantra } from "../types";
import { TARPANA_STEPS, VEDIC_MANTRAS } from "../data";
import { Droplet, Compass, Heart, BookOpen, Clock, Play, RotateCcw, Plus, Trash2, Globe, Sparkles, ArrowLeft, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { createAudioContext } from "../lib/audio-utils";

interface TarpanaGuideProps {
  onMantraSelect: (mantra: Mantra) => void;
  activeMantraId: string;
}

interface AncestralName {
  id: string;
  name: string;
  relation: string;
}

export default function TarpanaGuide({ onMantraSelect, activeMantraId }: TarpanaGuideProps) {
  const [activeTradition, setActiveTradition] = useState<"vedic" | "avestan" | "roman" | "norse">("vedic");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [libationCount, setLibationCount] = useState(0);
  const [showRipple, setShowRipple] = useState(false);
  const [customAncestors, setCustomAncestors] = useState<AncestralName[]>([
    { id: "1", name: "Departed Grandparents", relation: "Lineage Paternal" },
    { id: "2", name: "Spiritual Ancestors & Mentors", relation: "Linguistic Guard" }
  ]);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldRelation, setNewFieldRelation] = useState("");

  // Filter steps by active tradition
  const steps = TARPANA_STEPS.filter(s => s.tradition === activeTradition);
  const activeStep = steps[currentStepIndex] || steps[0];

  useEffect(() => {
    setCurrentStepIndex(0);
  }, [activeTradition]);

  // Web Audio Synthesizer for water dripping and sploshing
  const playDripSound = () => {
    try {
      const ctx = createAudioContext();
      if (!ctx) return;
      
      // Synthesis of a water drop: rapidly rising sine wave then falling away
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      // Starts medium, sweeps up then down
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + 0.08);
      osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.25);

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.warn("Drip sound failed:", e);
    }
  };

  const triggerLibation = () => {
    setLibationCount(prev => prev + 1);
    setShowRipple(true);
    playDripSound();
    setTimeout(() => {
      setShowRipple(false);
    }, 1200);
  };

  const addAncestor = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFieldName.trim()) return;
    const newAnc: AncestralName = {
      id: Date.now().toString(),
      name: newFieldName.trim(),
      relation: newFieldRelation.trim() || "Ancestor"
    };
    setCustomAncestors([...customAncestors, newAnc]);
    setNewFieldName("");
    setNewFieldRelation("");
  };

  const removeAncestor = (id: string) => {
    setCustomAncestors(customAncestors.filter(a => a.id !== id));
  };

  const selectStepMantra = () => {
    if (activeStep.targetMantraId) {
      const matching = VEDIC_MANTRAS.find(m => m.id === activeStep.targetMantraId);
      if (matching) {
        onMantraSelect(matching);
      }
    } else {
      // Find default tarpana for this tradition
      const defaultMantra = VEDIC_MANTRAS.find(m => m.tradition === activeTradition && m.id.includes("water"));
      if (defaultMantra) onMantraSelect(defaultMantra);
    }
  };

  return (
    <div className="space-y-6 text-left">
      
      {/* Tradition Selector Bar */}
      <div className="flex flex-wrap gap-2 bg-slate-900/30 p-2 border border-slate-900/80 rounded-2xl">
        {(["vedic", "avestan", "roman", "norse"] as const).map(trad => {
          const isActive = activeTradition === trad;
          const titles = {
            vedic: "Vedic Sanskrit",
            avestan: "Zoroastrian Avestan",
            roman: "Greco-Roman Latin",
            norse: "Norse Runic"
          };
          return (
            <button
              key={trad}
              onClick={() => setActiveTradition(trad)}
              className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold tracking-wider font-mono cursor-pointer transition uppercase text-center ${
                isActive
                  ? "bg-cyan-600 text-slate-950 shadow-md shadow-cyan-500/10 border border-cyan-400/20"
                  : "bg-slate-950/60 text-slate-400 hover:text-white border border-slate-900"
              }`}
            >
              {titles[trad]}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* Procedural Water Altar & Remembrance (5 Columns) */}
        <div className="lg:col-span-5 bg-slate-950/80 border border-slate-900 rounded-3xl p-6 flex flex-col justify-between items-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-grid-pattern opacity-5 pointer-events-none" />
          
          <div className="w-full flex justify-between items-center mb-4">
            <span className="text-[10px] text-cyan-400 font-mono tracking-widest uppercase block">
              // HYDRAULIC SACRED ALTAR
            </span>
            <div className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-[10px] font-mono text-slate-400">
              Libations: <span className="text-cyan-400 font-bold">{libationCount}</span>
            </div>
          </div>

          {/* Interactive Water Ripple Altar */}
          <div className="relative w-60 h-60 flex items-center justify-center my-4 bg-slate-950 border border-slate-900 rounded-full overflow-hidden shadow-inner shadow-cyan-500/5">
            
            {/* Soft pool bg */}
            <div className="absolute inset-4 rounded-full bg-cyan-950/15 border border-cyan-900/20" />

            {/* Concentric Animated Ripples */}
            <AnimatePresence>
              {showRipple && (
                <>
                  <motion.div
                    initial={{ scale: 0.1, opacity: 0.8 }}
                    animate={{ scale: 1.8, opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="absolute w-24 h-24 border border-cyan-400/40 rounded-full"
                  />
                  <motion.div
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 2.2, opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.6, delay: 0.2, ease: "easeOut" }}
                    className="absolute w-24 h-24 border border-cyan-500/25 rounded-full"
                  />
                </>
              )}
            </AnimatePresence>

            {/* Procedural Vector Icons corresponding to Tradition */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-8">
              
              {/* Vedic: Gokarna hand mudra pouring water */}
              {activeTradition === "vedic" && (
                <svg viewBox="0 0 100 100" className="w-full h-full opacity-40">
                  <path d="M 50,20 Q 35,45 50,75 Q 65,45 50,20" fill="none" stroke="#22d3ee" strokeWidth="1.5" />
                  <circle cx="50" cy="45" r="4" fill="#0891b2" />
                  <path d="M 40,55 Q 50,45 60,55" fill="none" stroke="#0891b2" strokeWidth="1" />
                </svg>
              )}

              {/* Avestan: Spring roots */}
              {activeTradition === "avestan" && (
                <svg viewBox="0 0 100 100" className="w-full h-full opacity-40">
                  <path d="M 50,85 L 50,45 M 50,70 L 30,55 M 50,60 L 70,50 M 50,45 Q 35,30 25,35 M 50,45 Q 65,25 75,30" fill="none" stroke="#4ade80" strokeWidth="2" />
                </svg>
              )}

              {/* Roman: Patera bowl */}
              {activeTradition === "roman" && (
                <svg viewBox="0 0 100 100" className="w-full h-full opacity-40">
                  <ellipse cx="50" cy="50" rx="35" ry="12" fill="none" stroke="#a1a1aa" strokeWidth="2" />
                  <ellipse cx="50" cy="50" rx="20" ry="7" fill="none" stroke="#71717a" strokeWidth="1" />
                </svg>
              )}

              {/* Norse: Drinking horn */}
              {activeTradition === "norse" && (
                <svg viewBox="0 0 100 100" className="w-full h-full opacity-40">
                  <path d="M 25,30 C 35,30 50,45 45,75 C 38,70 30,55 25,30 Z" fill="none" stroke="#b45309" strokeWidth="1.8" />
                  <path d="M 45,75 L 55,85" stroke="#d97706" strokeWidth="2" />
                </svg>
              )}

            </div>

            {/* Glowing Drop Icon in center */}
            <motion.div
              animate={{
                y: [0, -6, 0]
              }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="relative"
            >
              <Droplet className="h-10 w-10 text-cyan-400 fill-cyan-500/20 drop-shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
            </motion.div>
          </div>

          <button
            onClick={triggerLibation}
            className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-slate-950 font-mono font-bold uppercase tracking-widest text-xs py-3 px-5 rounded-xl cursor-pointer transition shadow-md shadow-cyan-500/10 flex items-center justify-center gap-2"
          >
            <Droplet className="h-4 w-4" />
            <span>Pour Water Libation</span>
          </button>
        </div>

        {/* Step Guide & Ancestral Remembrance (7 Columns) */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          
          {/* Main Procedural Step Card */}
          <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-6 relative flex flex-col justify-between min-h-[300px]">
            <div className="absolute top-0 right-0 h-32 w-32 bg-cyan-500/5 blur-3xl pointer-events-none" />

            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-slate-900 pb-3">
                <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-widest">
                  Step {activeStep.stepNumber} of {steps.length} — {activeStep.category}
                </span>

                <div className="flex items-center gap-1">
                  <Globe className="h-3 w-3 text-slate-500" />
                  <span className="text-[10px] font-mono text-slate-400 capitalize">{activeStep.tradition} Tarpana</span>
                </div>
              </div>

              {/* Native Script Display */}
              <div className="space-y-2 py-2">
                <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wide font-mono">
                  {activeStep.title}
                </h2>
                <p className="text-xl font-medium text-slate-200 leading-relaxed font-display text-left">
                  {activeStep.sanskrit}
                </p>
                <p className="text-xs font-mono text-cyan-400/80 leading-normal italic text-left">
                  {activeStep.iast}
                </p>
              </div>

              {/* Meaning & Procedure */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-900/60 pt-4 text-xs font-light">
                <div className="space-y-1.5">
                  <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider block font-bold">// SACRED ACTION:</span>
                  <p className="text-slate-300 leading-relaxed pl-1.5 border-l border-slate-800">{activeStep.procedure}</p>
                </div>
                <div className="space-y-1.5">
                  <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider block font-bold">// METAPHYSICAL MEANING:</span>
                  <p className="text-slate-400 leading-relaxed pl-1.5 border-l border-slate-800 italic">{activeStep.spiritualMeaning}</p>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-col sm:flex-row items-center gap-4 mt-6 border-t border-slate-900 pt-4">
              
              <button
                onClick={selectStepMantra}
                className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-cyan-400 font-mono text-[10px] font-bold uppercase tracking-wider py-2 px-4 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5"
              >
                <BookOpen className="h-3.5 w-3.5" />
                <span>Load Liturgy to Osc</span>
              </button>

              <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
                <button
                  disabled={currentStepIndex === 0}
                  onClick={() => setCurrentStepIndex(p => p - 1)}
                  className="flex-1 sm:flex-initial bg-slate-950 hover:bg-slate-900 border border-slate-800 text-slate-400 p-2 rounded-xl transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                
                <button
                  disabled={currentStepIndex === steps.length - 1}
                  onClick={() => setCurrentStepIndex(p => p + 1)}
                  className="flex-1 sm:flex-initial bg-slate-950 hover:bg-slate-900 border border-slate-800 text-slate-400 p-2 rounded-xl transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

            </div>
          </div>

          {/* Ancestor Remembrance Ledger */}
          <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-900 pb-3">
              <span className="text-[10px] text-cyan-400 font-mono tracking-widest uppercase block">
                // ANCESTRAL REMEMBRANCE LEDGER
              </span>
              <span className="text-[10px] font-mono text-slate-500 uppercase">
                {customAncestors.length} Recorded
              </span>
            </div>

            <div className="max-h-36 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
              {customAncestors.map((anc) => (
                <div key={anc.id} className="flex justify-between items-center bg-slate-900/20 border border-slate-900/60 p-2.5 rounded-xl">
                  <div>
                    <h5 className="text-xs font-bold text-slate-200">{anc.name}</h5>
                    <span className="text-[9px] font-mono text-slate-500 uppercase">{anc.relation}</span>
                  </div>
                  <button
                    onClick={() => removeAncestor(anc.id)}
                    className="text-slate-500 hover:text-rose-400 transition p-1.5 rounded-lg cursor-pointer hover:bg-rose-500/5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Form to add */}
            <form onSubmit={addAncestor} className="grid grid-cols-1 sm:grid-cols-12 gap-2 pt-2 border-t border-slate-900/40">
              <input
                type="text"
                placeholder="Ancestor's Name"
                value={newFieldName}
                onChange={e => setNewFieldName(e.target.value)}
                className="sm:col-span-6 bg-slate-950 border border-slate-900 text-xs px-3 py-2 rounded-xl text-slate-200 focus:outline-none focus:border-cyan-500/50"
              />
              <input
                type="text"
                placeholder="Relation / lineage"
                value={newFieldRelation}
                onChange={e => setNewFieldRelation(e.target.value)}
                className="sm:col-span-4 bg-slate-950 border border-slate-900 text-xs px-3 py-2 rounded-xl text-slate-200 focus:outline-none focus:border-cyan-500/50"
              />
              <button
                type="submit"
                className="sm:col-span-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-cyan-400 text-xs font-mono font-bold uppercase p-2 rounded-xl cursor-pointer transition flex items-center justify-center"
              >
                <Plus className="h-4 w-4" />
              </button>
            </form>
          </div>

        </div>

      </div>
    </div>
  );
}
